import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ProgressBar } from '../../../../components/general';

import useSampleProjectData from '../../../../hooks/useSampleProjectData';
import useVbData from '../../../../hooks/useVbData';

import { Person } from '../../../../types';

import { getGenerateText } from '../../components/getText';

interface Props {
  completePeople: Person[];
  incompletePeople: Person[];
  generatedPeople: Person[];
  handleSampleGenerationActions: (data: any) => void;
}

const GenerateSubStep: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const {
    completePeople,
    incompletePeople,
    generatedPeople,
    handleSampleGenerationActions,
  } = props;

  // sample project data
  const { isSample, sampleProjectData } = useSampleProjectData(eventId);
  const [samplePeople, setSamplePeople] = useState<Person[]>(
    sampleProjectData.people ?? [],
  );

  const { toGeneratePeopleIds } = useVbData();

  const {
    sampleCompletePeople,
    sampleIncompletePeople,
    sampleGeneratedPeople,
  } = useMemo(() => {
    const sampleCompletePeople: Person[] = [];
    const sampleIncompletePeople: Person[] = [];
    const sampleGeneratedPeople: Person[] = [];

    samplePeople.forEach((person) => {
      if (person.isSelected) {
        if (person.isGenerated) sampleGeneratedPeople.push(person);
        else if (person.isComplete) sampleCompletePeople.push(person);
        else sampleIncompletePeople.push(person);
      }
    });

    return {
      sampleCompletePeople,
      sampleIncompletePeople,
      sampleGeneratedPeople,
    };
  }, [samplePeople]);

  // imitate generation
  useEffect(() => {
    if (
      isSample &&
      sampleProjectData.event?.stage === 'generation' &&
      !sampleProjectData.isVideosGenerated
    ) {
      const interval = setInterval(() => {
        setSamplePeople((prevPeople) => {
          const nextPeople = [...prevPeople];

          const person = nextPeople.find(
            (person) => person.isComplete && !person.isGenerated,
          );

          // if complete not-generated person exists
          if (person) {
            person.isGenerated = true;

            // last video was just 'generated'
            if (nextPeople.every((p) => p.isGenerated || !p.isComplete)) {
              clearInterval(interval);

              sampleProjectData.isVideosGenerated = true;
              sampleProjectData.people = nextPeople;

              handleSampleGenerationActions(sampleProjectData);
            }
          }

          return nextPeople;
        });
      }, 1500);

      return () => clearInterval(interval);
    }
  }, [sampleProjectData, handleSampleGenerationActions, isSample]);

  const { generateText, progress } = getGenerateText({
    completePeople: isSample ? sampleCompletePeople : completePeople,
    incompletePeople: isSample ? sampleIncompletePeople : incompletePeople,
    generatedPeople: isSample ? sampleGeneratedPeople : generatedPeople,
    toGeneratePeopleIds,
  });

  return (
    <React.Fragment>
      <div>{generateText}</div>

      {progress < 100 && (
        <div className="w-full flex flex-col gap-4 text-center">
          <ProgressBar progress={progress} />

          <div className="font-bold uppercase">Generating</div>
        </div>
      )}
    </React.Fragment>
  );
};

export default GenerateSubStep;
